import React from "react";
import styled from "styled-components";
import { Spinner } from "components/ui";
import Tooltip, { TooltipPosition } from "./Tooltip";
import { colorTransition, disabledState, focusRing, iconSlot, unselectable } from "./base/mixins";

export interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
	/** Accessible name. An icon-only control has no label, so this is required. */
	label: string;
	/** Purple icon — marks an action (import, save, export, add) rather than a tool. §6. */
	action?: boolean;
	selected?: boolean;
	loading?: boolean;
	/** Rich tooltip. Without one the `label` still shows as the native title. */
	tooltip?: { text: React.ReactNode; position?: TooltipPosition };
	children: React.ReactNode;
}

/**
 * A square 32px tap target holding one 19px icon.
 *
 * Replaces `components/ui`'s IconButton, which inverted when disabled (dark fill,
 * near-white glyph) and coloured `name`-prop icons differently from children.
 * Here every icon is children and every state fades rather than inverts.
 */
const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
	({ label, action = false, selected = false, loading = false, tooltip, children, ...rest }, ref) => {
		const button = (
			<StyledIconButton
				ref={ref}
				type="button"
				aria-label={label}
				aria-pressed={selected || undefined}
				aria-busy={loading || undefined}
				title={tooltip ? undefined : label}
				{...rest}
				disabled={rest.disabled || loading}
				$action={action}
				$selected={selected}
			>
				<span className="hds-iconbtn__icon">
					{loading ? <Spinner maxSize="16px" /> : children}
				</span>
			</StyledIconButton>
		);

		if (!tooltip) return button;
		return (
			<Tooltip text={tooltip.text} position={tooltip.position ?? "bottom"}>
				{button}
			</Tooltip>
		);
	},
);

IconButton.displayName = "IconButton";

const StyledIconButton = styled.button<{ $action: boolean; $selected: boolean }>`
	${unselectable}
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: none;
	width: var(--size-iconbutton);
	height: var(--size-iconbutton);
	padding: 0;
	border: var(--border-default);
	border-radius: var(--radius-md);
	/* §8 "Icon button": selected is a filled lime chip with a white glyph — the
	   one white-on-lime pairing in the system. Unselected is a white chip. */
	background: ${({ $selected }) =>
		$selected ? "var(--color-primary-fill)" : "var(--color-surface)"};
	border-color: ${({ $selected }) =>
		$selected ? "var(--color-primary-fill)" : "var(--color-border)"};
	color: ${({ $selected, $action }) => {
		if ($selected) return "var(--color-icon-on-primary)";
		return $action ? "var(--color-secondary)" : "var(--color-text)";
	}};
	cursor: pointer;
	box-shadow: none;
	${colorTransition}
	${focusRing}
	${disabledState}

	.hds-iconbtn__icon {
		${iconSlot}
		/* Smaller than the shared 24px icon slot: this control's own tap target
		   is 32px, not the 40px it inherited iconSlot's 24px icon from. */
		width: var(--size-iconbutton-icon);
		height: var(--size-iconbutton-icon);
	}

	/* §6 migration: a legacy glyph bakes a hex fill into every path, so it cannot
	   inherit the colour set above and the selected state has to reach into it.
	   The vendored wrapper renders its svg as a *direct* child of the icon slot,
	   where a new-set glyph sits inside a span — that is what tells the two sets
	   apart, and why this must not be a blanket svg rule: a stroke-only glyph
	   given a fill would flood in solid. Retire it with the last name in
	   Icon/legacy.ts. */
	${({ $selected }) =>
		$selected &&
		`
		.hds-iconbtn__icon > svg,
		.hds-iconbtn__icon > svg:hover {
			path,
			polygon,
			circle,
			ellipse,
			rect {
				fill: var(--color-icon-on-primary);
				stroke: var(--color-icon-on-primary);
			}
		}
	`}

	&:hover:not(:disabled) {
		background: ${({ $selected }) =>
			$selected ? "var(--color-primary-fill-hover)" : "var(--color-hover-ground)"};
		border-color: ${({ $selected }) =>
			$selected ? "var(--color-primary-fill-hover)" : "var(--color-border-strong)"};
	}
`;

export default IconButton;
